"use client";
import Button from "@/components/Cards/Button";
import Newsletter from "@/components/Cards/Newsletter";
import ErrorModal from "@/components/ErrorModal";
import FAQAccordion from "@/components/FAQAccordion";
import { TextInput } from "@/components/Inputs/TextInput";
import InfoModal from "@/components/Modal";
import PlayerInPage from "@/components/Player/PlayerInPage";
import SideBySideText from "@/components/Text/SideBySideText";
import Testimonial from "@/components/Text/Testimonial";
import Text from "@/components/Text/Text";
import Triangles from "@/components/Triangles";
import { ButtonDark, LongButton } from "@/stories/Button.stories";
import { NewsletterYellow } from "@/stories/Newsletter.stories";
import { SBSVideoLeftTxt, SBSVideoRightTxt } from "@/stories/SideBySide.stories";
import { useForm, zodResolver } from "@mantine/form";
import { useRouter } from "next/navigation";
import { useState } from "react";
import FAQ from "../../../../public/content/FAQ.json";
import { z } from "zod";
import { sendGTMEvent } from "@/components/Analytics";

export default function ForschungsfrageLayout({
    sendEmail,
}: {
    sendEmail: (subject: string, data: object) => Promise<void>;
}) {
    const schema = z.object({
        opinion: z.string(),
        name: z.string(),
        surname: z.string(),
        email: z
            .string()
            .email()
            .refine((email) => email.length > 0, {
                message: "Bitte geben Sie eine gültige Email-Adresse ein",
            }),
    });
    const form = useForm({
        initialValues: {
            opinion: "",
            name: "",
            surname: "",
            email: "",
        },
        validate: zodResolver(schema),
    });

    const router = useRouter();
    const [openModal, setopenModal] = useState(false);
    const [openThankyouModal, setopenThankyouModal] = useState(false);
    const [error, setError] = useState(false);
    return (
        <>
            <div className="bg-yellow w-full space-y-1 md:px-2  flex-col justify-center items-center gap-5 inline-flex">
                <SideBySideText
                    backgroundColor={SBSVideoLeftTxt.args?.backgroundColor}
                    elementLeft={
                        <div className="flex justify-center">
                            <PlayerInPage
                                videoId="ce3c86c3801b34a07f3ee7cf0da6364d"
                                thumbnail="/images/thumbnails/frageThumbnail1.jpg"
                            />
                        </div>
                    }
                    elementRight={
                        <>
                            <Text
                                paragraphs={[
                                    "Die einfache Frage, warum es einigen Menschen gut geht und anderen nicht, beschäftigt mich seit meiner Kindheit. Mein fotografisches Gedächtnis, die starke und sehr schnelle Wahrnehmung und meine Art, alles zu hinterfragen, haben mir bei meiner Arbeit immer schon sehr geholfen. ",
                                    "Anders gesagt, ich musste genau verstehen, wieso dass seelische Verletzungen bei einigen  entstehen und bei anderen nicht. Aber auch, was ich tun muss, damit sie wieder repariert werden können. ",
                                ]}
                                hasText={true}
                                title={"Die richtige Frage war entscheidend"}
                                titleSize="text-xl3 lg:text-5xl"
                                paddings="px-3 lg:w-1/2"
                                hasSubTitle={false}
                                leading="leading-10 lg:leading-14"
                                leadingText="leading-7 lg:leading-8.5"
                            />
                        </>
                    }
                />
            </div>
            <div className="bg-yellow">
                <Triangles backgroundColor="bg-orange" buttomToTop={false} />
            </div>
            <div className="bg-orange flex justify-center">
                <div className="container md:px-20 w-full lg:space-y-0 space-y-1 px-3 py-10 lg:py-20 flex-col  lg:gap-0 gap-5 inline-flex">
                    <Text
                        hasPretitle={false}
                        hasSubTitle={false}
                        hasText={true}
                        title="Warum Ethik in der Energiefeldmechanik wichtig ist"
                        paragraphs={[
                            "Um die volle Wirkung der Energiefeld-Instandsetzung zu entfalten, müssen die folgenden ethischen Grundsätze der Energiefeldmechanik erfüllt sein: ",
                            "Die Ursachen zu lösen, die natürlichen Prozesse zu fördern – dabei keine Energie zu senden oder zu übertragen, den Bauplan des Energiefeldes zu beachten, nicht das Energiefeld zu manipulieren, da dies für ein Energiefeld ein Zwang ist und langfristig schadet.",
                            "Als Energiefeldmechanik bezeichne ich alle individuellen Prozesse, die das gesamte Energiefeld und deren Wechselwirkungen beinhalten.",
                            "Der Zustand der eigenen Energiefelder definiert, wie man auf Situationen und das Leben reagiert. Sprich, ob es einem gut geht oder eben nicht.",
                        ]}
                        paddings="px-3 lg:px-60 py-5"
                        leading="leading-10 lg:leading-14"
                        leadingText="leading-7 lg:leading-8.5"
                        hasReadMore={true}
                        paragraphSlice={2}
                    />
                    <div className="lg:px-60">
                        <Button
                            backgroundColor={"bg-darkGray"}
                            hasIcon={LongButton.args?.hasIcon}
                            border={LongButton.args?.border}
                            rounded={LongButton.args?.rounded}
                            textColor={LongButton.args?.textColor}
                            textSize={LongButton.args?.textSize}
                            size={LongButton.args?.size}
                            text="forschungsbasierte arbeitsweise"
                            paddings="lg:py-4 py-3 px-2 lg:px-10"
                            as="a"
                            href="/de/arbeitsweise/mechanische-grundsaetze"
                            onClick={() => {
                                sendGTMEvent({
                                    event: "cta_click",
                                    section_title:
                                        "Warum Ethik in der Energiefeldmechanik wichtig ist",
                                    section_index: "3",
                                    cta_text: "forschungsbasierte arbeitsweise",
                                    cta_link: `${process.env.NEXT_PUBLIC_URL}/de/arbeitsweise/mechanische-grundsaetze`,
                                });
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="bg-orange">
                <Triangles backgroundColor="bg-turquoise" buttomToTop={true} />
            </div>
            <div className="bg-turquoise flex justify-center">
                <div className="container w-full space-y-1 lg:px-0 flex-col justify-center items-center gap-5 inline-flex">
                    <Testimonial
                        text="Um die Forschungsfrage beantworten zu können, musste ich zuerst lernen, ein Energiefeld genügend lange, ohne zu interpretieren, zu beobachten . Nur so erkennt man die Funktionalitäten. Das erübrigt eine persönlich geprägte Interpretation, welche subjektiv ist und darum nicht der Sichtweise des Energiefeldes des Klienten entspricht, sondern die Sichtweise des Therapeuten wiedergibt."
                        description="Energiefeldspezialist seit 1998"
                        differentTextColor="text-lightYellow"
                        wordsInDifferentColor={[
                            "Energiefeld",
                            "beobachten",
                            "so",
                            "erkennt",
                            "Funktionalitäten.",
                        ]}
                    />
                </div>
            </div>
            <div className="bg-turquoise">
                <Triangles backgroundColor="bg-blue" buttomToTop={false} />
            </div>
            <div className="bg-blue w-full space-y-1 md:px-2 flex-col justify-center items-center gap-5 inline-flex">
                <SideBySideText
                    backgroundColor={SBSVideoRightTxt.args?.backgroundColor}
                    elementLeft={SBSVideoRightTxt.args?.elementLeft}
                    elementRight={
                        <div className="flex justify-center">
                            <PlayerInPage
                                videoId="978e996321855b1e2c875852adcd687b"
                                thumbnail="/images/thumbnails/frageThumbnail2.jpg"
                            />
                        </div>
                    }
                />
            </div>

            <div className="bg-blue">
                <Triangles backgroundColor="bg-white" buttomToTop={true} />
            </div>

            <div className="bg-white flex justify-center">
                <div className="container w-full px-3 pt-16 lg:px-20 ">
                    <div className="text-left md:text-center lg:text-center text-black text-xl3 lg:text-5xl font-bold   lg:leading-14">
                        FAQ
                    </div>
                    <p className="text-left md:text-center lg:text-center text-black text-xl1 font-normal  text-lg lg:leading-8.5">
                        Hast du Fragen zur Forschung?{" "}
                    </p>
                    <p className="text-left md:text-center lg:text-center text-black text-xl1 font-normal  text-lg lg:leading-8.5">
                        Kein Problem, hier erhältst du Antworten darauf.
                    </p>
                    <div className="flex justify-center items-center my-10">
                        <FAQAccordion
                            // @ts-ignore
                            FAQItem={
                                FAQ.FAQ.find((item) => item.page === "Forschungsfrage")
                                    ?.FAQ
                            }
                            // FAQItem={FAQ}
                        />
                    </div>
                </div>
            </div>
            <div className="bg-white">
                <Triangles backgroundColor="bg-yellow" buttomToTop={false} />
            </div>
            <InfoModal
                onClose={() => {
                    setopenModal(false);
                }}
                opened={openModal}
                size="xl"
            >
                <>
                    <form
                        className="space-y-2 my-3"
                        onSubmit={form.onSubmit((values) => {
                            const data = {
                                opinion: values.opinion,
                                name: values.name,
                                surname: values.surname,
                                email: values.email,
                            };
                            sendEmail(
                                "Haben dir diese Antworten nicht weitergeholfen?",
                                data,
                            )
                                .then(() => {
                                    setopenModal(false);
                                    setopenThankyouModal(true);
                                })
                                .catch((e) => {
                                    <ErrorModal
                                        open={error}
                                        onClose={() => setError(false)}
                                    />;
                                });
                        })}
                    >
                        <p className="self-stretch text-xl3 lg:text-5xl py-2 font-bold  leading-14">
                            Haben dir diese Antworten nicht weitergeholfen?
                        </p>
                        <TextInput
                            type="textarea"
                            id="opinion"
                            name="opinion"
                            label="Deine Frage"
                            placeholder="Hier eingeben"
                            {...form.getInputProps("opinion")}
                        />
                        <TextInput
                            type="text"
                            id="name"
                            name="name"
                            label="Vorname"
                            placeholder="Hier eingeben"
                            {...form.getInputProps("name")}
                        />
                        <TextInput
                            type="text"
                            id="surname"
                            name="surname"
                            label="Nachname"
                            placeholder="Hier eingeben"
                            {...form.getInputProps("surname")}
                        />
                        <TextInput
                            type="text"
                            id="email"
                            name="email"
                            label="E-mail"
                            placeholder="Hier eingeben"
                            {...form.getInputProps("email")}
                        />
                        <div className="flex py-5 justify-end">
                            <Button
                                backgroundColor={ButtonDark.args?.backgroundColor}
                                hasIcon={false}
                                textSize={ButtonDark.args?.textSize}
                                size={ButtonDark.args?.size}
                                paddings={ButtonDark.args?.paddings}
                                textColor={ButtonDark.args?.textColor}
                                text="Senden"
                            />
                        </div>
                    </form>
                </>
            </InfoModal>

            <InfoModal
                onClose={() => {
                    setopenThankyouModal(false);
                }}
                opened={openThankyouModal}
                size="xl"
            >
                <>
                    <Text
                        hasPretitle={false}
                        title="Vielen Dank für deine Blog-Idee!"
                        paragraphs={[
                            "Ich melde mich so schnell wie möglich bei dir.",
                            "Dein Thema werde ich gerne in die Videoproduktion aufnehmen. Bitte beachte, dass es zu Verzögerungen bei der Veröffentlichung kommen kann.",
                            "Ich melde mich wieder bei dir, sobald der Blogeitrag veröffentlich wurde. ",
                            "Liebe Grüsse",
                            "Rico Brunner",
                        ]}
                        paddings=""
                    />
                    <div className="flex py-5 justify-end">
                        <Button
                            backgroundColor={ButtonDark.args?.backgroundColor}
                            hasIcon={false}
                            textSize={ButtonDark.args?.textSize}
                            size={ButtonDark.args?.size}
                            paddings={ButtonDark.args?.paddings}
                            textColor={ButtonDark.args?.textColor}
                            text="Home"
                            as="a"
                            href="/"
                        />
                    </div>
                </>
            </InfoModal>

            <div className="bg-yellow w-full space-y-1 px-3 lg:px-20  flex-col justify-center items-center gap-5 inline-flex">
                <Newsletter
                    backgroundColor={NewsletterYellow.args?.backgroundColor}
                    buttonText="Jetzt FRAGE STELLEN"
                    title="Haben dir diese Antworten
                    nicht weitergeholfen?"
                    description="Dann sende mir deine Frage zur Forschung."
                    hasCheckbox={NewsletterYellow.args?.hasCheckbox}
                    hasDescription={NewsletterYellow.args?.hasDescription}
                    hasInput={NewsletterYellow.args?.hasInput}
                    hasSubText={NewsletterYellow.args?.hasSubText}
                    onClickButton={() => {
                        setopenModal(true);
                        sendGTMEvent({
                            event: "cta_click",
                            section_title:
                                "Haben dir diese Antworten nicht weitergeholfen?",
                            section_index: "7",
                            cta_text: "Jetzt FRAGE STELLEN",
                            cta_link: "modal",
                        });
                    }}
                    buttonPaddings="py-4 px-6"
                />
            </div>
            <div className="bg-yellow">
                <Triangles backgroundColor="bg-darkGray" buttomToTop={true} />
            </div>
        </>
    );
}
